import { html, isServer, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import type { TabVariant } from "../index";
import style from "./tab-group.css?inline";

let tabGroupCount = 1;

@customElement("eko-tab-group")
class TabGroup extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private handleKeyDown = (event: any) => {
    const tabs = Array.from(this.querySelectorAll("eko-tab")) as any[];
    const currentIndex = tabs.indexOf(this.selectedTab);

    if (!this.selectedTab?.matches(":focus-visible")) return;
    if (currentIndex === -1) return;

    let targetIndex = -1;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        targetIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        targetIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
        break;
      default:
        return;
    }

    if (targetIndex >= 0) {
      const targetTab = tabs[targetIndex];
      targetTab.focus();
      this.selectTab(targetTab);
    }
  };

  private handleTabClick = (event: Event) => {
    const tab = event.target as HTMLElement;
    if (tab.tagName.toLowerCase() !== "eko-tab") return;
    // Don't select the tab if:
    //  - It's already selected
    //  - Is a link (has an href attribute) as those pages will set active as an attribute on the tab when the page loads
    //  - Is a trigger for a dropdown (has a slot="trigger") as the dropdown trigger should not be considered "active" when the dropdown is open.
    //    only when one of its links is clicked
    if (
      !this.isSelectedTab(tab) &&
      !tab.hasAttribute("href") &&
      tab.getAttribute("slot") !== "trigger"
    ) {
      this.selectTab(tab);
    }
  };

  private tabGroupId = `eko-tab-group-${tabGroupCount++}`;

  @property({ reflect: true, type: Boolean }) expand: boolean = false;
  @property({ reflect: true, type: Boolean, attribute: "no-padding-bottom" })
  noPaddingBottom: boolean = false;
  @state() selectedTab: Element | null = this.initialSelectedTab;
  @property({ reflect: true }) size: "sm" | "md" = "md";
  @property({ reflect: true }) variant: TabVariant = "default";

  private isSelectedTab(tab: Element) {
    return tab.id === this.selectedTab?.id;
  }

  private selectTab(tab: Element) {
    this.selectedTab = tab;
    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: {
          selected: this.selectedTab,
        },
      })
    );
  }

  private setupPanels() {
    const panels = this.querySelectorAll("eko-tab-panel");
    const selectedPanel = this.selectedTab?.getAttribute("aria-controls");
    panels.forEach((panel: any, index: number) => {
      panel.setAttribute("id", `${this.tabGroupId}-panel-${index + 1}`);
      panel.setAttribute("aria-labelledby", `${this.tabGroupId}-tab-${index + 1}`);
      panel.setAttribute("aria-hidden", panel.getAttribute("id") !== selectedPanel);
      panel.toggleAttribute("no-padding-bottom", this.noPaddingBottom);
    });
  }

  private setupTabs() {
    const tabs = this.querySelectorAll("eko-tab");

    // Set initial selected tab if none is set (handles late initialization)
    if (!this.selectedTab && tabs.length > 0) {
      this.selectedTab = this.querySelector("eko-tab[active]") || tabs[0];
    }

    tabs.forEach((tab: any, index: number) => {
      tab.setAttribute("id", `${this.tabGroupId}-tab-${index + 1}`);
      tab.setAttribute("aria-controls", `${this.tabGroupId}-panel-${index + 1}`);
      tab.setAttribute("size", this.size);
      tab.setAttribute("variant", this.variant);
      tab.toggleAttribute("active", this.isSelectedTab(tab));
      tab.setAttribute("tabindex", this.isSelectedTab(tab) ? "0" : "-1");
    });
  }

  render() {
    return html`<div class="eko-tab-group" part="root" id=${this.tabGroupId} role="tablist">
      <div
        class="eko-tab-group__tabs"
        part="tabs"
        @click=${this.handleTabClick}
        @keydown=${this.handleKeyDown}>
        <slot @slotchange=${this.setupTabs}></slot>
      </div>
      <div class="eko-tab-group__panels" part="panels">
        <slot name="panel" @slotchange=${this.setupPanels}></slot>
      </div>
    </div>`;
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("selectedTab")) {
      this.setupTabs();
      this.setupPanels();
    }

    if (changedProperties.has("variant")) {
      this.setupTabs();
    }

    if (changedProperties.has("size")) {
      this.setupTabs();
    }

    if (changedProperties.has("noPaddingBottom")) {
      this.setupPanels();
    }
  }

  private get initialSelectedTab() {
    if (isServer) return null;

    return this.querySelector("eko-tab[active]") || this.querySelector("eko-tab");
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-tab-group": TabGroup;
  }
}

export default TabGroup;
